import styled, { css, keyframes } from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  page: "#f5f7fc",
  white: "#ffffff",
  title: "#293142",
  body: "#59647a",
  muted: "#8b94a8",
  lightMuted: "#a7afbf",
  line: "#e1e6ef",
  brand: "#ef4f5f",
  brandSoft: "#fdeef0",
  success: "#149447",
  successSoft: "#eaf9ef",
};

const MOBILE = "@media (max-width: 480px)";

export const MarkEntryHeader = styled.header`
  display: flex;
  min-height: 76px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 18px 28px;
  background: ${COLORS.white};
  box-shadow: 0 3px 10px rgba(41, 49, 66, 0.04);

  ${MOBILE} {
    min-height: 64px;
    padding: 14px 18px;
  }
`;

export const MarkEntryHeaderTitle = styled.h1`
  overflow: hidden;
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 17px;
  line-height: 1.25;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 15px;
  }
`;

export const ShipmentSummaryHeader = styled.div`
  padding: 20px 20px 4px;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${COLORS.title};
    font-family: ${fontFamilies.bold};
    font-size: 16px;
  }

  span {
    margin-top: 4px;
    color: ${COLORS.body};
    font-size: 13px;
  }

  ${MOBILE} {
    padding: 16px 16px 2px;

    strong {
      font-size: 15px;
    }

    span {
      font-size: 12px;
    }
  }
`;

export const ShipmentSummaryDivider = styled.div`
  border-top: 1px solid #f1f3f7;
  margin: 14px 20px 0;

  ${MOBILE} {
    margin: 12px 16px 0;
  }
`;

export const InvoiceCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const InvoiceCard = styled.article`
  display: grid;
  min-width: 0;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  border-radius: 22px;
  padding: 14px 16px;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.08);

  ${MOBILE} {
    grid-template-columns: 48px minmax(0, 1fr) auto;
    gap: 10px;
    border-radius: 17px;
    padding: 12px;
  }
`;

export const InvoiceCardExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 4px;
  background: transparent;
  color: ${COLORS.brand};
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 2;
  }
`;

export const SwipeConfirmBar = styled.footer`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid #eef1f6;
  padding: 17px 28px 20px;
  background: ${COLORS.white};
  box-shadow: 0 -7px 20px rgba(41, 49, 66, 0.05);

  ${MOBILE} {
    padding: 13px 16px 16px;
  }
`;

export const SwipeErrorText = styled.p`
  margin: 0;
  color: ${COLORS.brand};
  font-size: 13px;
  text-align: center;
`;

export const SwipeTrack = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 58px;
  border: 1.5px solid ${({ $disabled }) => ($disabled ? COLORS.line : COLORS.brand)};
  border-radius: 16px;
  background: ${({ $disabled }) => ($disabled ? "#f1f3f7" : COLORS.brandSoft)};
  touch-action: none;

  ${MOBILE} {
    height: 52px;
    border-radius: 14px;
  }
`;

export const SwipeLabel = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $disabled }) => ($disabled ? COLORS.lightMuted : COLORS.brand)};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  pointer-events: none;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const SwipeThumb = styled.div`
  position: absolute;
  top: 50%;
  left: 4px;
  display: flex;
  width: 66px;
  height: 50px;
  align-items: center;
  justify-content: center;
  gap: 1px;
  border-radius: 10px;
  background: ${({ $disabled }) => ($disabled ? COLORS.lightMuted : COLORS.brand)};
  color: ${COLORS.white};
  cursor: ${({ $busy, $disabled }) => ($disabled ? "default" : $busy ? "wait" : "grab")};

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2.6;
    margin-left: -7px;
  }

  svg:first-child {
    margin-left: 0;
  }

  ${({ $dragging }) =>
    !$dragging &&
    css`
      transition: transform 0.2s ease;
    `}

  ${MOBILE} {
    width: 58px;
    height: 44px;
    left: 4px;
  }
`;

const popIn = keyframes`
  from { transform: scale(0.85); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

export const SuccessState = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  text-align: center;
  animation: ${popIn} 0.25s ease;
`;

export const SuccessIcon = styled.img`
  display: block;
  width: 80px;
  height: 80px;
  margin: 0 auto 18px;
`;

export const SuccessSubtitle = styled.p`
  max-width: 280px;
  margin: 8px 0 0;
  color: ${COLORS.body};
  font-size: 14px;
  line-height: 1.4;

  ${MOBILE} {
    font-size: 13px;
  }
`;

export const OutcomeIconCircle = styled.div`
  display: flex;
  width: 112px;
  height: 112px;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.08);

  img {
    display: block;
    width: 48px;
    height: 48px;
  }

  ${MOBILE} {
    width: 80px;
    height: 80px;
  }
`;

export const OutOfRangeSubtitle = styled.p`
  max-width: 280px;
  margin: 8px 0 0;
  color: #e86c37;
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;
  line-height: 1.4;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const LocationHintCard = styled.div`
  width: 100%;
  max-width: 320px;
  margin-top: 18px;
  border-radius: 16px;
  padding: 6px 18px;
  background: #fdeee0;
  text-align: left;
`;

export const LocationHintRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(191, 41, 56, 0.1);
  }

  svg {
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    margin-top: 1px;
    color: #e86c37;
  }

  span {
    color: ${COLORS.title};
    font-family: ${fontFamilies.semiBold};
    font-size: 14px;
    line-height: 1.4;
    text-align: left;
  }

  ${MOBILE} {
    span {
      font-size: 13px;
    }
  }
`;

export const OutcomeRetryButton = styled.button`
  min-height: 48px;
  margin-top: 22px;
  border: 0;
  border-radius: 13px;
  padding: 0 30px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  cursor: pointer;
`;

export const PublicPageState = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 28px;
  text-align: center;
`;

export const MarkEntryFormState = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
  align-items: center;
  padding: 40px 32px;
  text-align: center;
`;

export const MarkEntryFormCard = styled.div`
  width: 100%;
  max-width: 340px;
  margin-top: 26px;
  border-radius: 22px;
  padding: 18px;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.08);
  text-align: left;
`;

export const MarkEntryInput = styled.input`
  width: 100%;
  min-height: 52px;
  border: 1px solid ${COLORS.line};
  border-radius: 13px;
  padding: 0 16px;
  background: ${COLORS.white};
  color: ${COLORS.title};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;

  &::placeholder {
    color: ${COLORS.lightMuted};
    font-family: ${fontFamilies.regular};
  }

  &:focus {
    outline: none;
    border-color: ${COLORS.brand};
  }
`;

export const MarkEntryInputHint = styled.p`
  margin: 10px 0 18px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f5f7fc;
  color: ${COLORS.body};
  font-size: 13px;
  line-height: 1.4;

  ${MOBILE} {
    font-size: 12px;
  }
`;
